import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";

type SessionCore = {
  id: string;
  dataSessao?: string;
  tipoSessao?: string;
  legislatura?: string;
  [key: string]: any;
};

type Attendance = {
  id: string;
  nome?: string;
  nomeParlamentar?: string;
  funcaoPartidaria?: string;
  momento?: any;
  [key: string]: any;
};

type SessionPautaRef = {
  id: string; // subdocument id in `sessoes/{roomId}/pautas`
  idPauta?: string; // reference to top-level pauta id
  [key: string]: any;
};

type Pauta = {
  id: string;
  numero?: string | number;
  autor?: string;
  pautaLegislativa?: string;
  expressoes?: string;
  opcoesVotacao?: string;
  statusAtual?: string;
  [key: string]: any;
};

export function stripHtml(html: string | undefined): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/\.&nbsp\./g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

export async function fetchSessionCore(
  roomId: string
): Promise<SessionCore | null> {
  const db = getFirestore();
  const ref = doc(db, "users", "belacruz@eleg", "sessoes", `${roomId}`);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as any) };
}

export async function fetchSessionAttendance(
  roomId: string
): Promise<Attendance[]> {
  const db = getFirestore();
  const ref = collection(
    db,
    "users",
    "belacruz@eleg",
    "sessoes",
    `${roomId}`,
    "presencaParlamentares"
  );
  const qs = await getDocs(ref);
  const rows: Attendance[] = qs.docs.map((d) => ({
    id: d.id,
    ...(d.data() as any),
  }));
  // sort by time if available
  return rows.sort((a, b) => {
    const ta = (a as any).momento ? new Date((a as any).momento).getTime() : 0;
    const tb = (b as any).momento ? new Date((b as any).momento).getTime() : 0;
    return ta - tb;
  });
}

export async function fetchSessionPautas(roomId: string): Promise<Pauta[]> {
  const db = getFirestore();
  const ref = collection(
    db,
    "users",
    "belacruz@eleg",
    "sessoes",
    `${roomId}`,
    "pautas"
  );
  const qs = await getDocs(ref);
  const sessionPautas: SessionPautaRef[] = qs.docs.map((d) => ({
    id: d.id,
    ...(d.data() as any),
  }));

  // For each, try to join with the top-level pauta document to enrich fields
  const enriched: Pauta[] = await Promise.all(
    sessionPautas.map(async (sp) => {
      const pautaId = sp.idPauta || sp.id;
      try {
        const pautaRef = doc(
          getFirestore(),
          "users",
          "belacruz@eleg",
          "pautas",
          `${pautaId}`
        );
        const pautaSnap = await getDoc(pautaRef);
        if (pautaSnap.exists()) {
          const data = pautaSnap.data() as any;
          return {
            id: pautaSnap.id,
            ...data,
            expressoes: stripHtml(data.expressoes),
          };
        }
      } catch (_) {
        // ignore and fallback
      }
      return { id: pautaId, ...(sp as any) } as Pauta;
    })
  );

  return enriched;
}

export async function fetchSessionSummary(roomId: string): Promise<{
  sessao: SessionCore | null;
  presencas: Attendance[];
  pautas: Pauta[];
}> {
  const [sessao, presencas, pautas] = await Promise.all([
    fetchSessionCore(roomId),
    fetchSessionAttendance(roomId),
    fetchSessionPautas(roomId),
  ]);

  return { sessao, presencas, pautas };
}

export default {
  fetchSessionCore,
  fetchSessionAttendance,
  fetchSessionPautas,
  fetchSessionSummary,
  stripHtml,
};
